import {
  ArrowLeftRight,
  Banknote,
  Camera,
  CheckSquare,
  FolderKanban,
  LayoutDashboard,
  Map,
  NotebookPen,
  Package,
  Radio,
  Settings,
  Truck,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProject } from "@/hooks/useProjects";
import { useCameraPlans } from "@/hooks/useCameraPlans";
import { useTasks } from "@/hooks/useTasks";
import { useQuote } from "@/hooks/useQuotes";
import { useInvoice } from "@/hooks/useInvoices";
import { useMeetingNote } from "@/hooks/useMeetingNotes";
import { StatusBadge, type ProjectStatus } from "@/components/ProjectBadges";
import { formatProjectName } from "@/lib/format-project-name";
import { PROJECT_SWITCHER_OPEN_EVENT } from "@/components/ProjectSwitcher";
import { Button } from "@/components/ui/button";
import {
  cameraPlanHref,
  invoiceHref,
  meetingNoteHref,
  quoteHref,
} from "../hrefs";
import { matchContextPath } from "../match";
import type { ContextParams, NavContextDef, NavItem } from "../types";

export const projectPath = (id: string) => `/projects/${id}`;

/**
 * Child pages open below a section (quote, invoice, meeting note). Only the
 * open one is listed, so its id comes from the current path.
 */
function useOpenChildIds(base: string) {
  const { pathname } = useLocation();
  const rest = pathname.startsWith(`${base}/`)
    ? pathname.slice(base.length + 1).split("/")
    : [];
  const [section, sub, childId] = rest;
  return {
    quoteId: section === "finance" && sub === "quotes" ? childId : undefined,
    invoiceId:
      section === "finance" && sub === "invoices" ? childId : undefined,
    meetingNoteId:
      section === "notes" && sub === "meeting-notes" ? childId : undefined,
  };
}

/** Open (not done) tasks of the project, for the Tasks badge (L6). */
function useOpenTaskCount(projectId: string) {
  const { data: tasks } = useTasks();
  return useMemo(
    () =>
      tasks.filter(
        (task) =>
          task.state !== "done" &&
          task.projects?.some((link) => link.project?.id === projectId),
      ).length,
    [tasks, projectId],
  );
}

function useOpenQuoteItem(
  projectId: string,
  quoteId: string | undefined,
): NavItem[] {
  const { data: quote } = useQuote(quoteId);
  if (!quoteId) return [];
  const versions = [...(quote?.versions ?? [])]
    .filter((version) => version.status !== "rejected")
    .sort((a, b) => b.version_number - a.version_number);
  return [
    {
      key: `finance/quotes/${quoteId}`,
      label: quote ? `${quote.quote_number} – ${quote.name}` : "Quote",
      href: quoteHref(projectId, quoteId),
      children: versions.map((version) => ({
        key: `finance/quotes/${quoteId}/versions/${version.id}`,
        label: version.internal_name
          ? `V${version.version_number} · ${version.internal_name}`
          : `V${version.version_number}`,
        href: quoteHref(projectId, quoteId, version.id),
      })),
    },
  ];
}

function useOpenInvoiceItem(
  projectId: string,
  projectNumber: string | null | undefined,
  invoiceId: string | undefined,
): NavItem[] {
  const { data: invoice } = useInvoice(invoiceId);
  if (!invoiceId) return [];
  const number = invoice
    ? [projectNumber, invoice.invoice_number].filter(Boolean).join("-")
    : "";
  return [
    {
      key: `finance/invoices/${invoiceId}`,
      label: invoice ? `${number} – ${invoice.title || "Untitled"}` : "Invoice",
      href: invoiceHref(projectId, invoiceId),
    },
  ];
}

function useOpenMeetingNoteItem(
  projectId: string,
  noteId: string | undefined,
): NavItem[] {
  const { data: note } = useMeetingNote(noteId);
  if (!noteId) return [];
  return [
    {
      key: `notes/meeting-notes/${noteId}`,
      label: note?.title || "Meeting note",
      href: meetingNoteHref({ id: noteId, project_id: projectId }),
    },
  ];
}

function useProjectItems({ id }: ContextParams): NavItem[] {
  const { isEmployee } = useAuth();
  const { data: cameraPlans } = useCameraPlans(id);
  const { data: project } = useProject(id);
  const base = projectPath(id);
  const openChild = useOpenChildIds(base);
  const openTaskCount = useOpenTaskCount(id);
  const quoteItems = useOpenQuoteItem(id, openChild.quoteId);
  const invoiceItems = useOpenInvoiceItem(
    id,
    project?.number,
    openChild.invoiceId,
  );
  const meetingNoteItems = useOpenMeetingNoteItem(id, openChild.meetingNoteId);

  const items: NavItem[] = [
    {
      key: "overview",
      label: "Overview",
      href: base,
      exact: true,
      icon: LayoutDashboard,
    },
    {
      key: "settings",
      label: "Settings",
      href: `${base}/settings`,
      icon: Settings,
    },
    {
      key: "notes",
      label: "Notes",
      href: `${base}/notes/meeting-notes`,
      activePrefix: `${base}/notes`,
      icon: NotebookPen,
      children: [
        {
          key: "notes/meeting-notes",
          label: "Meeting notes",
          href: `${base}/notes/meeting-notes`,
          children: meetingNoteItems,
        },
        { key: "notes/notes", label: "Notes", href: `${base}/notes/notes` },
        {
          key: "notes/debriefing",
          label: "Debriefing",
          href: `${base}/notes/debriefing`,
        },
        {
          key: "notes/related",
          label: "Related",
          href: `${base}/notes/related`,
        },
      ],
    },
    {
      key: "tasks",
      label: "Tasks",
      href: `${base}/tasks`,
      icon: CheckSquare,
      badge: openTaskCount || null,
    },
  ];

  if (isEmployee) {
    items.push({
      key: "finance",
      label: "Finance",
      href: `${base}/finance/quotes`,
      activePrefix: `${base}/finance`,
      icon: Banknote,
      children: [
        {
          key: "finance/quotes",
          label: "Quotes",
          href: `${base}/finance/quotes`,
          children: quoteItems,
        },
        {
          key: "finance/invoices",
          label: "Invoices",
          href: `${base}/finance/invoices`,
          children: invoiceItems,
        },
        {
          key: "finance/time-tracking",
          label: "Time tracking",
          href: `${base}/finance/time-tracking`,
        },
      ],
    });
  }

  items.push(
    {
      key: "plan",
      label: "Plan",
      href: `${base}/plan/camera-plans`,
      activePrefix: `${base}/plan`,
      icon: Map,
      children: [
        {
          key: "plan/camera-plans",
          label: "Camera plans",
          href: `${base}/plan/camera-plans`,
          icon: Camera,
          children: cameraPlans.map((plan) => ({
            key: `plan/camera-plans/${plan.id}`,
            label: plan.name || "Untitled plan",
            href: cameraPlanHref(id, plan.id),
          })),
        },
        {
          key: "plan/network-coverage",
          label: "Network coverage",
          href: `${base}/plan/network-coverage`,
        },
      ],
    },
    {
      key: "equipment",
      label: "Equipment",
      href: `${base}/equipment/packing`,
      activePrefix: `${base}/equipment`,
      icon: Package,
      children: [
        {
          key: "equipment/packing",
          label: "Packing",
          href: `${base}/equipment/packing`,
        },
        {
          key: "equipment/rigging",
          label: "Rigging",
          href: `${base}/equipment/rigging`,
        },
        {
          key: "equipment/carnet-ata",
          label: "Carnet ATA",
          href: `${base}/equipment/carnet-ata`,
        },
      ],
    },
    {
      key: "vehicles",
      label: "Vehicles",
      href: `${base}/vehicles`,
      icon: Truck,
    },
    {
      key: "people",
      label: "People",
      href: `${base}/people/overview`,
      activePrefix: `${base}/people`,
      icon: Users,
      children: [
        {
          key: "people/overview",
          label: "Overview",
          href: `${base}/people/overview`,
        },
        {
          key: "people/functions",
          label: "Functions",
          href: `${base}/people/functions`,
          testId: "functions-tab-trigger",
        },
        {
          key: "people/availability",
          label: "Availability",
          href: `${base}/people/availability`,
        },
        {
          key: "people/planning",
          label: "Meals & Hotel",
          href: `${base}/people/planning`,
        },
        ...(isEmployee
          ? [
              {
                key: "people/intercom",
                label: "Intercom",
                href: `${base}/people/intercom`,
              },
            ]
          : []),
      ],
    },
    {
      key: "streams",
      label: "Streams",
      href: `${base}/streams/feeds`,
      activePrefix: `${base}/streams`,
      icon: Radio,
      children: [
        {
          key: "streams/feeds",
          label: "Feeds & Signals",
          href: `${base}/streams/feeds`,
        },
        ...(isEmployee
          ? [
              {
                key: "streams/cloud",
                label: "Cloud resources",
                href: `${base}/streams/cloud`,
              },
              {
                key: "streams/studio-configs",
                label: "Studio configs",
                href: `${base}/streams/studio-configs`,
              },
              {
                key: "streams/playout-log",
                label: "Playout log",
                href: `${base}/streams/playout-log`,
              },
            ]
          : []),
      ],
    },
  );

  return items;
}

function useProjectHeader({ id }: ContextParams) {
  const { data: project } = useProject(id);
  if (!project) return {};
  const name = formatProjectName(project.name, project.event_start_date);
  return {
    kicker: project.number,
    title: name,
    trailTitle: project.number ? `${project.number} ${name}` : name,
    // Confirmed is the normal case; only flag the others.
    status:
      project.status && project.status !== "confirmed" ? (
        <StatusBadge status={project.status as ProjectStatus} />
      ) : null,
  };
}

function ProjectContextSwitcher() {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0"
      title="Switch project"
      aria-label="Switch project"
      data-testid="context-switcher"
      onClick={() =>
        window.dispatchEvent(
          new CustomEvent(PROJECT_SWITCHER_OPEN_EVENT, {
            detail: { keepSection: true },
          }),
        )
      }
    >
      <ArrowLeftRight className="h-3.5 w-3.5" />
    </Button>
  );
}

export const projectContext: NavContextDef = {
  type: "project",
  typeLabel: "Project",
  icon: FolderKanban,
  match: (pathname) =>
    matchContextPath("/projects/:id", pathname, {
      id: ["calendar", "groups", "new"],
    }),
  key: ({ id }) => `project:${id}`,
  base: ({ id }) => projectPath(id),
  parent: () => ({ label: "Projects", href: "/projects" }),
  useHeader: useProjectHeader,
  useItems: useProjectItems,
  Switcher: ProjectContextSwitcher,
};
